import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getHomeViewRole } from '@app/selectors/home';
import { useTour } from '@app/shared/tour';
import type { TourConfig, TourContextValue } from '@app/shared/tour';
import { buildHomeTourSteps } from './homeTourSteps';
import { HOME_TOUR_BUTTONS } from './homeTourContent';
import {
  getHomeTourOverlayColor,
  getHomeTourOverlayOpacity,
  getHomeTourPopoverClass,
} from './homeTourTheme';
import {
  trackHomeTourCompleted,
  trackHomeTourDismissed,
  trackHomeTourPromptShown,
  trackHomeTourSkipped,
  trackHomeTourStarted,
  trackHomeTourStepViewed,
} from './homeTourAnalytics';
import {
  removeHomeSpacesTourProxy,
  resolveHomeSpacesTourElement,
} from './resolveHomeSpacesTourElement';
import {
  cancelHomeScrollAnim,
  scrollHomeToTop,
  scrollHomeToTourTarget,
} from './homeTourScroll';

type UseHomeTourArgs = {
  isReady: boolean;
};

const useHomeTour = ({ isReady }: UseHomeTourArgs): TourContextValue => {
  const viewRole = useSelector(getHomeViewRole);
  const viewRoleRef = useRef(viewRole);
  viewRoleRef.current = viewRole;

  const config: TourConfig = useMemo(
    () => ({
      id: 'home',
      steps: () => buildHomeTourSteps(viewRoleRef.current),
      buttons: HOME_TOUR_BUTTONS,
      theme: {
        overlayColor: getHomeTourOverlayColor,
        overlayOpacity: getHomeTourOverlayOpacity,
        popoverClass: getHomeTourPopoverClass,
      },
      analytics: {
        // Read role at fire time so PROMPT_SHOWN / step events match the
        // settled Home view role, not a stale closure from an earlier render.
        onPromptShown: () => trackHomeTourPromptShown(viewRoleRef.current),
        onStarted: (source) =>
          trackHomeTourStarted(source, viewRoleRef.current),
        onStepViewed: (params) =>
          trackHomeTourStepViewed({
            ...params,
            role: viewRoleRef.current,
          }),
        onCompleted: (params) =>
          trackHomeTourCompleted({
            ...params,
            role: viewRoleRef.current,
          }),
        onSkipped: (params) =>
          trackHomeTourSkipped({
            ...params,
            role: viewRoleRef.current,
          }),
        onDismissed: () => trackHomeTourDismissed(viewRoleRef.current),
      },
      scrollStrategy: scrollHomeToTourTarget,
      onHighlighted: ({ stepId, driver: d }) => {
        if (stepId === 'spaces') {
          resolveHomeSpacesTourElement();
          d.refresh();
        }
      },
      onAfterDestroy: () => {
        cancelHomeScrollAnim();
        removeHomeSpacesTourProxy();
        scrollHomeToTop();
      },
      animateMs: 700,
      progressText: '{{current}}/{{total}}',
    }),
    [viewRole]
  );

  return useTour(config, viewRole, isReady);
};

export default useHomeTour;
